import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import clsx from 'clsx';
import { CameraPlaybackPanel } from '@components/CameraPlaybackPanel';
import { StatePanel } from '@components/StatePanel';
import { StatusPill } from '@components/StatusPill';
import { camerasApi } from '@api/cameras';
import { HttpError } from '@api/http';
import type { Camera } from '@api/types';
import { useHasPermission } from '@auth/guards';

export default function CameraPlaybackPage(): JSX.Element {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const cameraId = Number(id);
  const canRecordingView = useHasPermission('recording_view');
  const [camera, setCamera] = useState<Camera | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadCamera = useCallback(
    async (signal?: AbortSignal) => {
      if (!Number.isInteger(cameraId) || cameraId <= 0) {
        setCamera(null);
        setError('Invalid camera id');
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);
      try {
        const nextCamera = await camerasApi.get(cameraId, signal);
        setCamera(nextCamera);
      } catch (err) {
        if (signal?.aborted) return;
        const message =
          err instanceof HttpError
            ? err.message
            : err instanceof Error
              ? err.message
              : 'Failed to load camera';
        setError(message);
      } finally {
        if (!signal?.aborted) setIsLoading(false);
      }
    },
    [cameraId],
  );

  useEffect(() => {
    const controller = new AbortController();
    void loadCamera(controller.signal);
    return () => controller.abort();
  }, [loadCamera]);

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden bg-bg">
      <header className="z-10 shrink-0 border-b border-border bg-bg/85 backdrop-blur">
        <div className="mx-auto flex max-w-screen-2xl flex-wrap items-center gap-3 px-6 py-3">
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className={clsx(
              'rounded-md border border-border bg-surface px-3 py-1.5 text-xs font-medium text-foreground',
              'transition-colors hover:border-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
            )}
          >
            Back to dashboard
          </button>

          <div>
            <h1 className="text-lg font-semibold text-foreground">
              {camera ? camera.name : 'Camera playback'}
            </h1>
            <p className="text-xs text-muted">
              {canRecordingView ? 'Live view and recorded timeline' : 'Live view'}
            </p>
          </div>

          {camera ? (
            <div className="ml-auto flex flex-wrap items-center gap-3 text-xs text-muted">
              <StatusPill status={camera.status} />
              <span className="capitalize">{camera.recordingMode}</span>
              {/* <span className="uppercase">{camera.storageTarget}</span> */}
            </div>
          ) : null}
        </div>
      </header>

      {error ? (
        <main className="flex min-h-0 flex-1 items-center justify-center overflow-hidden px-6 py-4">
          <StatePanel
            variant="error"
            title="Could not load playback"
            description={error}
            action={
              <button
                type="button"
                onClick={() => void loadCamera()}
                className="rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-foreground hover:border-accent"
              >
                Retry
              </button>
            }
          />
        </main>
      ) : isLoading ? (
        <main className="flex min-h-0 flex-1 items-center justify-center overflow-hidden px-6 py-4">
          <StatePanel title="Loading playback..." description="Fetching camera details and timeline." />
        </main>
      ) : camera ? (
        <main className="mx-auto flex min-h-0 w-full max-w-screen-2xl flex-1 flex-col overflow-hidden px-6 py-4">
          <CameraPlaybackPanel camera={camera} />
        </main>
      ) : null}
    </div>
  );
}
